import { useState, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Search, ChevronDown, MoreVertical, X, Ban } from 'lucide-react';
import { estimatesApi } from '@/api/client';
import { formatCurrency, type EstimateStatus } from '@medical-counsellor/shared';

export function EstimatesPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showDropdown, setShowDropdown] = useState(false);
    const [statusFilter, setStatusFilter] = useState<EstimateStatus | 'ALL'>('ALL');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeActionMenu, setActiveActionMenu] = useState<string | null>(null);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [selectedEstimate, setSelectedEstimate] = useState<any>(null);
    const [declineReason, setDeclineReason] = useState('');
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const { data, isLoading } = useQuery({
        queryKey: ['estimates'],
        queryFn: () => estimatesApi.list(),
    });

    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status, declineReason }: { id: string; status: EstimateStatus; declineReason?: string }) =>
            estimatesApi.updateStatus(id, { status, declineReason }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['estimates'] });
            setShowDeclineModal(false);
            setDeclineReason('');
            setSelectedEstimate(null);
        },
    });

    const allEstimates = data?.data || [];

    // Filter estimates by status and search query
    const estimates = useMemo(() => {
        let filtered = allEstimates;

        if (statusFilter !== 'ALL') {
            filtered = filtered.filter((est: any) => est.status === statusFilter);
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter((est: any) =>
                est.referenceNumber.toLowerCase().includes(query) ||
                est.patientName.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [allEstimates, statusFilter, searchQuery]);

    // Get status badge styling
    const getStatusBadge = (status: EstimateStatus) => {
        const styles = {
            PENDING: 'bg-blue-100 text-blue-800',
            CONVERTED: 'bg-green-100 text-green-800',
            EXPIRED: 'bg-gray-100 text-gray-800',
            DECLINED: 'bg-red-100 text-red-800',
            CANCELLED: 'bg-orange-100 text-orange-800',
        };
        return styles[status] || 'bg-gray-100 text-gray-800';
    };

    // Calculate days until expiry
    const getDaysUntilExpiry = (expiresAt: Date | null) => {
        if (!expiresAt) return null;
        const now = new Date();
        const expiry = new Date(expiresAt);
        const diffTime = expiry.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const handleDecline = (estimate: any) => {
        setSelectedEstimate(estimate);
        setShowDeclineModal(true);
        setActiveActionMenu(null);
    };

    const handleCancel = (estimate: any) => {
        if (confirm('Are you sure you want to cancel this estimate?')) {
            updateStatusMutation.mutate({ id: estimate.id, status: 'CANCELLED' });
        }
        setActiveActionMenu(null);
    };

    const handleDeclineSubmit = () => {
        if (selectedEstimate) {
            updateStatusMutation.mutate({
                id: selectedEstimate.id,
                status: 'DECLINED',
                declineReason: declineReason.trim() || undefined,
            });
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Estimates</h1>
                    <p className="text-slate-600">View and manage cost estimates</p>
                </div>
                <div className="relative">
                    <button
                        onClick={() => setShowDropdown(!showDropdown)}
                        className="btn btn-primary btn-md flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        New Estimate
                        <ChevronDown className="w-4 h-4" />
                    </button>
                    {showDropdown && (
                        <>
                            <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowDropdown(false)}
                            />
                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 z-20">
                                <div className="py-1">
                                    <button
                                        onClick={() => {
                                            navigate('/estimates/new');
                                            setShowDropdown(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                    >
                                        <div className="font-medium">Standard Estimate</div>
                                        <div className="text-xs text-slate-500">From configured procedure</div>
                                    </button>
                                    <button
                                        onClick={() => {
                                            navigate('/estimates/custom');
                                            setShowDropdown(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                    >
                                        <div className="font-medium">Custom Estimate</div>
                                        <div className="text-xs text-slate-500">Manual entry for any procedure</div>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Search and filters */}
            <div className="card">
                <div className="card-body">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by reference number or patient name..."
                                className="input pl-10"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <select
                            className="input w-full sm:w-48"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as EstimateStatus | 'ALL')}
                        >
                            <option value="ALL">All statuses</option>
                            <option value="PENDING">Pending</option>
                            <option value="CONVERTED">Admitted</option>
                            <option value="EXPIRED">Expired</option>
                            <option value="DECLINED">Declined</option>
                            <option value="CANCELLED">Cancelled</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Estimates table */}
            <div className="card">
                {isLoading ? (
                    <div className="card-body">
                        <div className="animate-pulse space-y-4">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="h-16 bg-slate-100 rounded-lg" />
                            ))}
                        </div>
                    </div>
                ) : estimates.length === 0 ? (
                    <div className="card-body text-center py-16">
                        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900">No estimates found</h3>
                        <p className="text-slate-500 mt-1">Click "New Estimate" above to create your first estimate.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Reference</th>
                                    <th>Patient</th>
                                    <th>Procedure</th>
                                    <th>Location</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {estimates.map((estimate: any) => {
                                    const daysUntilExpiry = getDaysUntilExpiry(estimate.expiresAt);
                                    return (
                                        <tr key={estimate.id}>
                                            <td className="font-medium text-slate-900">
                                                {estimate.referenceNumber}
                                            </td>
                                            <td>
                                                <div>
                                                    <p className="font-medium">{estimate.patientName}</p>
                                                    {estimate.patientId && (
                                                        <p className="text-xs text-slate-500">{estimate.patientId}</p>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {estimate.isCustom
                                                    ? estimate.customProcedureName
                                                    : estimate.procedureConfig?.procedure?.name || '-'}
                                            </td>
                                            <td>{estimate.location?.name || '-'}</td>
                                            <td className="font-semibold text-slate-900">
                                                {formatCurrency(estimate.totalAmount)}
                                            </td>
                                            <td>
                                                <div className="space-y-1">
                                                    <span
                                                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                                                            estimate.status
                                                        )}`}
                                                    >
                                                        {estimate.status === 'CONVERTED' ? 'ADMITTED' : estimate.status}
                                                    </span>
                                                    {estimate.status === 'PENDING' && daysUntilExpiry !== null && (
                                                        <div className="text-xs text-slate-500">
                                                            {daysUntilExpiry > 0
                                                                ? `Expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`
                                                                : daysUntilExpiry === 0
                                                                    ? 'Expires today'
                                                                    : 'Expired'}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-slate-500">
                                                {new Date(estimate.createdAt).toLocaleDateString('en-IN', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    year: 'numeric',
                                                })}
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <NavLink
                                                        to={`/estimates/${estimate.id}`}
                                                        className="btn btn-ghost btn-sm"
                                                    >
                                                        View
                                                    </NavLink>
                                                    {estimate.status === 'PENDING' && (
                                                        <div className="relative">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const button = e.currentTarget;
                                                                    const rect = button.getBoundingClientRect();
                                                                    setDropdownPosition({
                                                                        top: rect.bottom + 8,
                                                                        left: rect.right - 192, // 192px = w-48 (12rem)
                                                                    });
                                                                    setActiveActionMenu(
                                                                        activeActionMenu === estimate.id ? null : estimate.id
                                                                    );
                                                                }}
                                                                className="btn btn-ghost btn-sm"
                                                            >
                                                                <MoreVertical className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Action Menu Dropdown - Rendered outside table to avoid overflow clipping */}
            {activeActionMenu && (
                <>
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setActiveActionMenu(null)}
                    />
                    <div
                        className="fixed bg-white rounded-lg shadow-xl border border-slate-200 z-50 w-56 animate-fade-in"
                        style={{
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                        }}
                    >
                        <div className="py-1">
                            <button
                                onClick={() => handleDecline(estimates.find((e: any) => e.id === activeActionMenu))}
                                className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-3 group"
                            >
                                <X className="w-4 h-4 text-slate-400 group-hover:text-red-600" />
                                <span className="font-medium">Decline Estimate</span>
                            </button>
                            <button
                                onClick={() => handleCancel(estimates.find((e: any) => e.id === activeActionMenu))}
                                className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-orange-50 hover:text-orange-700 transition-colors flex items-center gap-3 group"
                            >
                                <Ban className="w-4 h-4 text-slate-400 group-hover:text-orange-600" />
                                <span className="font-medium">Cancel Estimate</span>
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Decline Modal */}
            {showDeclineModal && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={() => {
                        setShowDeclineModal(false);
                        setDeclineReason('');
                        setSelectedEstimate(null);
                    }}
                >
                    <div
                        className="bg-white rounded-lg shadow-xl max-w-md w-full animate-fade-in"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            <div className="mb-6">
                                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                                    Decline Estimate
                                </h3>
                                <p className="text-sm text-slate-600">
                                    Declining estimate for <span className="font-medium text-slate-900">{selectedEstimate?.patientName}</span>
                                </p>
                            </div>

                            <div className="mb-6">
                                <label className="label">
                                    Reason (Optional)
                                </label>
                                <textarea
                                    value={declineReason}
                                    onChange={(e) => setDeclineReason(e.target.value)}
                                    placeholder="Enter reason for declining..."
                                    className="input h-32 resize-none"
                                    rows={4}
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    This information will be saved for future reference
                                </p>
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setShowDeclineModal(false);
                                        setDeclineReason('');
                                        setSelectedEstimate(null);
                                    }}
                                    className="btn btn-secondary btn-md"
                                    disabled={updateStatusMutation.isPending}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeclineSubmit}
                                    className="btn btn-primary btn-md"
                                    disabled={updateStatusMutation.isPending}
                                >
                                    {updateStatusMutation.isPending ? 'Declining...' : 'Decline Estimate'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
